import React from 'react';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EventIcon from '@mui/icons-material/EventOutlined';
import HotelIcon from '@mui/icons-material/HotelOutlined';
import PublicIcon from '@mui/icons-material/Public';
import TourIcon from '@mui/icons-material/Tour';
import { Box, Button, Card, CardContent, CircularProgress, Divider, Stack, Typography } from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';

import { HotelBundleComponent } from '~/types/bundles/bundle';

import TourOfferName from './TourOfferName';
import { regionName } from './regions';
import useBundleReadiness, { BUILDER_STEPS, BuilderStep } from './useBundleReadiness';

interface Props {
  readiness: ReturnType<typeof useBundleReadiness>;
  activeStep: BuilderStep;
  onGoToStep: (step: BuilderStep) => void;
}

function pluralise(count: number, noun: string) {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box color="text.secondary" pt={0.25}>
        {icon}
      </Box>
      <Box minWidth={0}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2" component="div">
          {children}
        </Typography>
      </Box>
    </Stack>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  );
}

function HotelLine({ hotel }: { hotel: HotelBundleComponent }) {
  const { data: offer } = useGetOffer(hotel.offer_id);
  return (
    <div>
      {offer?.name ?? 'Flash hotel'} · {pluralise(hotel.nights, 'night')} from day {(hotel.day_offset ?? 0) + 1}
    </div>
  );
}

const NEXT_LABELS: Record<BuilderStep, string> = {
  build: 'Add the tour and a hotel that share a region',
  departures: 'Choose the departures to sell',
  approval: 'Approve the bundle',
  content: 'Edit the content customers see',
  prices: 'Set the prices',
};

/** What the bundle is made of so far, and the one thing to do next. */
export default function BundleOverview({ readiness, activeStep, onGoToStep }: Props) {
  const { tour, hotels, departures, isChecking, checkError, blocked, regions, nextStep } = readiness;
  const nextLabel = BUILDER_STEPS.find((step) => step.key === nextStep)?.label;

  return (
    <Card variant="outlined" sx={{ position: { md: 'sticky' }, top: { md: 88 } }}>
      <CardContent>
        <Typography variant="overline" color="text.secondary">
          Bundle
        </Typography>
        <Stack spacing={2} mt={1}>
          <Row icon={<TourIcon fontSize="small" />} label="Tour">
            {tour ? <TourOfferName tourOfferId={tour.tour_offer_id} /> : <Empty>Not added yet</Empty>}
          </Row>

          <Row icon={<HotelIcon fontSize="small" />} label="Hotel">
            {hotels.length > 0 ? (
              hotels.map((hotel) => <HotelLine key={hotel.id} hotel={hotel} />)
            ) : (
              <Empty>Not added yet</Empty>
            )}
          </Row>

          <Row icon={<PublicIcon fontSize="small" />} label="Sells in">
            {!regions.hasTour || !regions.hasHotel ? (
              <Empty>Needs the tour and hotel</Empty>
            ) : regions.isLoading ? (
              <CircularProgress size={14} />
            ) : regions.error ? (
              <Typography variant="body2" color="warning.main">
                Couldn't load
              </Typography>
            ) : regions.shared.length > 0 ? (
              regions.shared.map(regionName).join(', ')
            ) : (
              <Typography variant="body2" color="error.main">
                No shared regions
              </Typography>
            )}
          </Row>

          <Row icon={<EventIcon fontSize="small" />} label="Departures">
            {departures.length === 0 ? (
              <Empty>None selected</Empty>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{pluralise(departures.length, 'departure')}</span>
                {isChecking ? (
                  <CircularProgress size={14} />
                ) : checkError ? (
                  <Typography variant="body2" color="warning.main" display="flex" alignItems="center" gap={0.5}>
                    <ErrorOutlineIcon fontSize="inherit" /> couldn't check
                  </Typography>
                ) : blocked.length > 0 ? (
                  <Typography variant="body2" color="error.main" display="flex" alignItems="center" gap={0.5}>
                    <ErrorOutlineIcon fontSize="inherit" /> {blocked.length} can't be sold
                  </Typography>
                ) : (
                  <Typography variant="body2" color="success.main" display="flex" alignItems="center" gap={0.5}>
                    <CheckCircleIcon fontSize="inherit" /> all available
                  </Typography>
                )}
              </Stack>
            )}
          </Row>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary">
          Next
        </Typography>
        <Typography variant="body2" fontWeight="medium" mb={nextStep === activeStep ? 0 : 1.5}>
          {NEXT_LABELS[nextStep]}
        </Typography>
        {nextStep !== activeStep && (
          <Button size="small" variant="outlined" endIcon={<ArrowForwardIcon />} onClick={() => onGoToStep(nextStep)}>
            Go to {nextLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
